<template>
    <div>
        <b-modal
            :visible="value"
            :id="modal_id"
            size="lg"
            @cancel="cancel"
            @close="cancel"
            @hidden="hidden"
            @shown="onModalShown"
            :no-close-on-esc="uploading_media"
            :no-close-on-backdrop="uploading_media"
        >
            <template #modal-title>
                Upload Media
            </template>

            <div class="row align-items-center">
                <div class="col-lg-12 order-lg-1 order-2">
                    <div class="mb-3">
                        New Media
                        <small class="text-muted d-block mb-1">Maximum file size 300 MB.</small>
                        <b-form-file
                            v-model="submit_data.file"
                            :state="Boolean(submit_data.file)"
                            placeholder="Choose a file or drop it here..."
                            drop-placeholder="Drop file here..."
                            accept="image/*,audio/*,video/*,application/zip"
                            @input="processFile"
                        ></b-form-file>
                        <b-alert
                            v-if="upload_file_size_alert"
                            variant="warning"
                            class="mt-2 mb-0"
                            show
                        >
                            {{ upload_file_size_alert }}
                        </b-alert>
                        <b-form-invalid-feedback :state="!submit_data.file ? false : null">
                            Media is required
                        </b-form-invalid-feedback>
                    </div>

                    <b-form-group label="Default Splash Image" label-for="splash_default_key">
                        <b-form-select
                            v-model="submit_data.splash_default_key"
                            @change="onDefaultSplashChange"
                        >
                            <b-form-select-option :value="null">None / Custom</b-form-select-option>
                            <b-form-select-option
                                v-for="opt in default_splash_options"
                                :key="opt.key"
                                :value="opt.key"
                            >
                                {{ opt.label }}
                            </b-form-select-option>
                        </b-form-select>
                        <b-alert
                            v-if="default_splash_load_failed"
                            variant="warning"
                            class="mt-2 mb-0"
                            show
                        >
                            Some default splash options could not be loaded. You can still upload a custom splash image.
                        </b-alert>
                    </b-form-group>

                    <b-form-group label="Splash Image" label-for="splash_file">
                        <b-form-file
                            v-model="submit_data.splash_file"
                            :disabled="!!submit_data.splash_default_key"
                            @input="onSplashFileChange"
                            placeholder="Choose a file or drop it here..."
                            drop-placeholder="Drop file here..."
                            accept="image/*"
                        />
                        <b-form-invalid-feedback
                            :state="selected_type === 'sims' && !(submit_data.splash_file || simsHasThumbnail) ? false : null"
                        >
                            Splash Image is required
                        </b-form-invalid-feedback>
                    </b-form-group>

                    <b-form-group
                        v-if="selected_type === 'video'"
                        label="Caption / Subtitle File"
                        label-for="caption_file"
                    >
                        <b-form-file
                            v-model="submit_data.caption_file"
                            placeholder="Choose a file or drop it here..."
                            drop-placeholder="Drop file here..."
                            accept=".vtt,.srt"
                        />
                    </b-form-group>

                    <b-form-group
                        label="Title"
                        label-for="title"
                    >
                        <b-form-input
                            type="text"
                            v-model="submit_data.title"
                        />
                        <b-form-invalid-feedback :state="!submit_data.title ? false : null">
                            Title is required
                        </b-form-invalid-feedback>
                    </b-form-group>

                    <b-form-group
                        label="Language"
                        label-for="language"
                    >
                        <language-multiselect
                            v-model="submit_data.language"
                            :options="languages"
                            @add="addLanguage"
                            @remove="removeLanguage"
                        />
                        <b-form-invalid-feedback :state="!submit_data.language ? false : null">
                            Language is required
                        </b-form-invalid-feedback>
                    </b-form-group>


                    <b-form-group
                        label="Media Type"
                        label-for="selected_type"
                    >
                        <select v-model="selected_type" class="form-control">
                            <option disabled value="">Please select one</option>
                            <option value="audio">Audio</option>
                            <option value="image">Image</option>
                            <option value="video">Video</option>
                            <option value="sims">Sims (ZIP file)</option>
                        </select>
                        <b-form-invalid-feedback :state="!selected_type ? false : null">
                            Type is required
                        </b-form-invalid-feedback>
                    </b-form-group>

                    <b-form-group
                        v-if="selected_type === 'image'"
                    >
                        <label>
                            Merlin ID(s)
                            <sup>
                                <font-awesome-icon
                                    :icon="['fad', 'info-circle']"
                                    content="For multiple Merlin ids, use comma separated values"
                                    class="text-secondary" v-tippy="{placement: 'top'}"
                                />
                            </sup>
                        </label>
                        <b-form-input
                            v-model="submit_data.merlin_ids"
                        />
                    </b-form-group>

                    <b-form-group
                        label="Tags"
                        label-for="tags"
                    >
                        <tag-multiselect
                            v-model="submit_data.tags"
                            :options="tags"
                        />
                    </b-form-group>

                    <b-form-group
                        v-if="selected_type !== 'sims'"
                        label="Description"
                        label-for="description"
                    >
                        <b-form-textarea v-model="submit_data.description" rows="2" max-rows="6" />
                    </b-form-group>

                    <b-form-group
                        v-if="selected_type === 'image'"
                        label="Alt"
                        label-for="alt"
                    >
                        <b-form-textarea v-model="submit_data.alt" rows="2" max-rows="6" />
                        <b-form-invalid-feedback :state="!submit_data.alt ? false : null">
                            Alt tag is required for images
                        </b-form-invalid-feedback>
                    </b-form-group>

                    <b-form-group
                        v-if="selected_type !== 'sims'"
                        label="Caption"
                        label-for="caption"
                    >
                        <b-form-textarea v-model="submit_data.caption" rows="2" max-rows="6" />
                    </b-form-group>

                    <b-form-group v-if="selected_type !== 'sims'">
                        <div class="d-flex justify-content-between">
                            <span class="mb-2">Credit</span>
                            <b-form-checkbox
                                @change="handlePublicDomainToggle"
                                v-model="submit_data.is_public_domain"
                                switch
                            >
                                Public Domain
                            </b-form-checkbox>
                        </div>
                        <b-form-textarea
                            v-model="submit_data.credit" rows="2" max-rows="6"
                            :disabled="submit_data.is_public_domain"
                        />
                    </b-form-group>

                    <b-form-group>
                        <div class="d-flex justify-content-between">
                            <span class="mb-2">Designer Notes</span>
                        </div>
                        <b-form-textarea v-model="submit_data.designer_notes" rows="2" max-rows="6" />
                    </b-form-group>

                    <b-form-group>
                        <div class="d-flex justify-content-between">
                            <span class="mb-2">Original File Name</span>
                        </div>
                        <b-form-input
                            type="text"
                            v-model="submit_data.merlin_title"
                        />
                    </b-form-group>
                </div>
            </div>

            <template #modal-footer>
                <div v-if="!uploading_media">
                    <b-button
                        variant="primary"
                        @click="uploadFile"
                        :disabled="validating()"
                    >
                        Upload Media
                    </b-button>
                </div>
                <div v-else>
                    <b-spinner small label="Uploading..."></b-spinner>
                    Uploading File - This may take a few minutes
                </div>
                <b-button
                    variant="light"
                    @click="cancel"
                    :disabled="uploading_media"
                >
                    Cancel
                </b-button>
            </template>
        </b-modal>
    </div>
</template>

<script>
import TagMultiselect from '@/components/TagMultiselect'
import {mapState} from 'vuex'
import LanguageMultiselect from '@/components/LanguageMultiselect'
import languages from '@/common/languages'
import JSZip from 'jszip'

/** Maximum upload size for media files (bytes). */
const MAX_MEDIA_UPLOAD_BYTES = 300 * 1024 * 1024

export default {
    data: () => ({
        /** Message under New Media `b-form-file` when `submit_data.file` exceeds max size. */
        upload_file_size_alert: null,
        /** [{ key, label }] default splash options loaded from the registry endpoint. */
        default_splash_options: [],
        /** Requirement 2.8 partial-load indicator: fewer than four options or a failed fetch. */
        default_splash_load_failed: false,
        submit_data: {
            id: '',
            title: '',
            description: '',
            alt: '',
            caption: '',
            credit: '',
            tags: [],
            is_public_domain: false,
            language: '',
            splash_file: null,
            splash_default_key: null,
            caption_file: null,
            file: null,
            url: null,
            designer_notes: '',
        },
        selected_type: null,
        languages: languages,
        uploading_media: false,
        simsHasThumbnail: false,
    }),
    props: [
        'value',
        'type'
    ],
    components: {
        TagMultiselect,
        LanguageMultiselect
    },
    computed: {
        ...mapState({
            tags: state => state.tags.tags,
        }),
        modal_id() {
            let modal_id = 'add-related-media-modal'
            return modal_id
        }
    },
    watch: {
        value: {
            handler(value) {
                // Reset form state whenever visibility toggles. Open-time loading of the
                // default splash options happens in the b-modal `shown` hook (onModalShown)
                // because some parents pin `:value="null"`, so this watcher cannot be relied
                // on to see a truthy open transition.
                this.resetForm();
            }
        },
    },
    methods: {
        fileExceedsMaxUpload(file) {
            return file && typeof file.size === 'number' && file.size > MAX_MEDIA_UPLOAD_BYTES
        },
        /**
         * Validates only `submit_data.file`. Sets `upload_file_size_alert` when over limit.
         * Does not clear the warning when `file` is empty — that happens after rejecting an oversized file and
         * clearing the input, which would otherwise flash the alert away.
         * @returns {boolean} true if the file should be rejected (oversized).
         */
        rejectOversizedMainFile(file) {
            if (!this.fileExceedsMaxUpload(file)) {
                if (file && typeof file.size === 'number') {
                    this.upload_file_size_alert = null
                }
                return false
            }
            const mb = MAX_MEDIA_UPLOAD_BYTES / (1024 * 1024)
            this.upload_file_size_alert = `Media file must be ${mb} MB or smaller.`
            return true
        },
        uploadsWithinMaxSize() {
            return !this.rejectOversizedMainFile(this.submit_data.file)
        },
        async loadDefaultSplashes() {
            try {
                const { data } = await this.axios.get('/api/media/default_splashes')
                this.default_splash_options = (data && data.default_splashes) || []
                this.default_splash_load_failed = this.default_splash_options.length < 4
            } catch (e) {
                this.default_splash_options = []
                this.default_splash_load_failed = true
            }
        },
        onDefaultSplashChange(key) {
            // Selecting a default clears + disables the file input (Req 5.1).
            if (key) {
                this.submit_data.splash_file = null
            }
            // Selecting None/custom re-enables the file input (Req 5.2); key is already null.
            this.submit_data.splash_default_key = key || null
        },
        onSplashFileChange(file) {
            // Choosing a file resets the picker to None/custom (Req 5.3).
            if (file) {
                this.submit_data.splash_default_key = null
            }
        },
        handlePublicDomainToggle(newVal) {
            // If the user is toggling on and there is an existing credit, show a confirmation dialog
            if (newVal && this.submit_data.credit) {
                this.$swal({
                    html: 'Are you sure you want to set this media to public domain? Doing so will delete any credit that currently exists for this media.',
                    showCancelButton: true,
                    confirmButtonText: 'Set As Public Domain',
                    cancelButtonText: 'Cancel',
                    reverseButtons: true
                }).then((result) => {
                    if (result.value) {
                        this.submit_data.is_public_domain = newVal;
                        this.submit_data.credit = '';
                    } else {
                        this.submit_data.is_public_domain = !newVal;
                    }
                })

            } else {
                this.submit_data.is_public_domain = newVal;
            }
        },
        resetForm() {
            this.upload_file_size_alert = null
            this.submit_data = {
                id: '',
                title: '',
                description: '',
                alt: '',
                caption: '',
                credit: '',
                is_public_domain: false,
                tags: [],
                language: '',
                splash_file: null,
                splash_default_key: null,
                caption_file: null,
                file: null,
                url: null,
                designer_notes: '',
            };
        },
        validating() {
            const {language, title, alt, file, splash_file} = this.submit_data;

            if (this.selected_type === 'image') {
                return !(file && language && title && alt);
            }

            if (this.selected_type === 'sims') {
                return !(file && language && title && (splash_file || this.simsHasThumbnail));
            }

            return !(file && language && title && this.selected_type);
        },
        addTags() {
            for (let tag of this.submit_data.tags) {
                this.axios.post('/api/media/add_tag/' + this.submit_data.id, {
                    tag_id: tag.id
                })
            }
        },
        addLanguage(new_language) {
            this.submit_data.language = new_language;
        },
        removeLanguage() {
            this.submit_data.language = {};
        },
        onModalShown() {
            // Reliable open hook (see watch.value note). Ensure a clean form, load the
            // default splash picker options, then focus (Req 2.1-2.4, 2.8).
            this.resetForm();
            this.loadDefaultSplashes();
            this.focusSearch();
        },
        focusSearch() {
            if (
                this.$refs['related-media-search'] &&
                this.$refs['related-media-search'].$refs['search-term']
            ) {
                this.$refs['related-media-search'].$refs['search-term'].focus()
            }
        },
        cancel(event) {
            event.preventDefault()
            if (this.uploading_media) {
                return
            }
            this.$emit('closeUpload');
            this.hide()
        },
        hide() {
            this.$emit('hideUploadMedia')
        },
        hidden() {
            this.$emit('hideUploadMedia')
        },
        async processFile(file) {
            if (this.rejectOversizedMainFile(file)) {
                this.$nextTick(() => {
                    this.submit_data.file = null
                })
                return
            }
            if (!file || !file.type) {
                return
            }

            if (file.type.includes('image')) {
                this.selected_type = 'image'
            } else if (file.type.includes('video')) {
                this.selected_type = 'video'
            } else if (file.type.includes('audio')) {
                this.selected_type = 'audio'
            } else if (file.type.includes('zip')) {
                this.selected_type = 'sims'
            }

            if (this.selected_type === 'sims') {
                await this.checkSimsFileForThumbnail(file)
            }
        },
        async checkSimsFileForThumbnail(file) {
            this.simsHasThumbnail = false
            const zipFile = await JSZip.loadAsync(file)
            if (!zipFile || !zipFile.files) {
                return
            }

            // check the zip file for a file called 'thumbnail.jpg'
            const matches = zipFile.filter((path) => path.endsWith('/thumbnail.jpg'))
            if (matches.length > 0) {
                this.simsHasThumbnail = true
            }
        },
        async uploadFile() {
            if (this.uploading_media || !this.uploadsWithinMaxSize()) {
                return
            }
            let file = null;
            let splash_file = null;
            let form_data = new FormData();
            file = this.submit_data.file;
            splash_file = this.submit_data.splash_file;

            let merlin_id_array = [];
            // format comma separated merlin_ids input into an array, only accept numbers
            if (this.submit_data.merlin_ids) {
                merlin_id_array = this.submit_data.merlin_ids
                    .split(',')
                    .map((id) => parseInt(id.trim(), 10))
                    .filter((id) => !isNaN(id));
            }

            form_data.append('file', file);
            if (this.submit_data.splash_default_key) {
                form_data.append('splash_default_key', this.submit_data.splash_default_key)
            } else if (splash_file) {
                form_data.append('splash_file', splash_file)
            }
            form_data.append('caption_file', this.submit_data.caption_file)
            form_data.append('title', this.submit_data.title);
            form_data.append('language', this.submit_data.language.language);
            form_data.append('description', this.submit_data.description);
            form_data.append('alt', this.submit_data.alt);
            form_data.append('caption', this.submit_data.caption);
            form_data.append('is_public_domain', this.submit_data.is_public_domain);
            form_data.append('credit', this.submit_data.credit);
            form_data.append('designer_notes', this.submit_data.designer_notes);

            if (!this.submit_data.merlin_title) {
                form_data.append('merlin_title', file.name);
            } else {
                form_data.append('merlin_title', this.submit_data.merlin_title);
            }

            if (merlin_id_array.length > 0) {
                form_data.append('merlin_ids', merlin_id_array);
            }

            this.submit_data.url = `//cdn.studiesweekly.com/online/resources/pod_media/${file.name}`

            try {
                this.uploading_media = true
                const response = await this.axios.post('/api/media/upload_media/', form_data)
                if (!response) {
                    throw new Error('Failed to upload media')
                }

                this.$bvToast.toast('Media was successfully uploaded', {
                    title: 'Success',
                    variant: 'success',
                    solid: false,
                    autoHideDelay: 3000,
                    appendToast: true
                })
                if (response.data.media_id) {
                    this.submit_data.id = response.data.media_id
                    if (this.submit_data.tags.length > 0) {
                        this.addTags()
                    }
                }
                this.$emit('link', this.submit_data)
                this.$emit('hideUploadMedia');
                this.$emit('closeUpload');
                this.$emit('getMedia');
                this.hide();
            } catch (e) {
                this.$bvToast.toast(
                    (e.response && e.response.data && e.response.data.message) || 'An error has occurred', {
                    title: 'Error',
                    variant: 'danger',
                    solid: false,
                    autoHideDelay: 3000,
                    appendToast: true
                })
            } finally {
                this.uploading_media = false;
            }
        },
    },
}

</script>
